<template>
  <div class="page-contact">
  <main>
      <section class="relative overflow-hidden bg-gray-50">
        <div class="absolute inset-0 bg-gradient-to-r from-brand/5 via-transparent to-brand/5"></div>
        <div class="mx-auto max-w-[1400px] px-4 py-16 lg:grid lg:grid-cols-2 lg:items-center lg:gap-12 lg:px-8 lg:py-20">
          <div class="relative z-10 space-y-4">
            <p class="text-xs font-semibold uppercase text-muted">Contact us</p>
            <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl">We respond fast—tell us how we can help.</h1>
            <p class="text-lg text-muted">Couples, vendors, and partners can reach our concierge team 7 days a week.</p>
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="rounded-2xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <p class="text-sm font-semibold text-ink">Concierge chat</p>
                <p class="text-sm text-muted">Average response in 2 hours</p>
              </div>
              <div class="rounded-2xl bg-white p-4 shadow-card ring-1 ring-gray-100">
                <p class="text-sm font-semibold text-ink">Vendor support</p>
                <p class="text-sm text-muted">Listing help, payouts, onboarding</p>
              </div>
            </div>
          </div>
          <div class="relative z-10 mt-10 lg:mt-0">
            <form class="space-y-4 rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <label class="text-sm font-semibold text-ink" for="contact-name">Name</label>
                  <input id="contact-name" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none" placeholder="Your name" />
                </div>
                <div>
                  <label class="text-sm font-semibold text-ink" for="contact-email">Email</label>
                  <input id="contact-email" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none" type="email" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <label class="text-sm font-semibold text-ink" for="contact-type">I’m reaching out as</label>
                <select id="contact-type" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink focus:border-brand focus:outline-none">
                  <option>Couple</option>
                  <option>Vendor</option>
                  <option>Partner</option>
                  <option>Press</option>
                </select>
              </div>
              <div>
                <label class="text-sm font-semibold text-ink" for="contact-message">Message</label>
                <textarea id="contact-message" rows="4" class="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm text-ink placeholder:text-muted focus:border-brand focus:outline-none" placeholder="How can we help?"></textarea>
              </div>
              <button class="w-full rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" type="button">Send message</button>
              <p class="text-xs text-muted">Prefer email? Reach us at <a class="text-brand" href="mailto:hello@invi8.com">hello@invi8.com</a>.</p>
            </form>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="grid gap-6 md:grid-cols-3">
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Support hours</p>
            <p class="mt-2 text-sm text-muted">Daily: 8am – 8pm PT</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Locations</p>
            <p class="mt-2 text-sm text-muted">San Francisco · New York · Remote</p>
          </div>
          <div class="rounded-3xl bg-gray-50 p-6 ring-1 ring-gray-100">
            <p class="text-sm font-semibold text-ink">Emergency line</p>
            <p class="mt-2 text-sm text-muted">Day-of issues? Call (555) 123-4567</p>
          </div>
        </div>
      </section>

      <section class="bg-gray-900">
        <div class="mx-auto max-w-[1400px] px-4 py-12 text-white lg:px-8">
          <div class="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase text-gray-400">FAQs</p>
              <h2 class="text-3xl font-semibold leading-tight">Answers for couples and vendors.</h2>
              <p class="text-lg text-gray-300">Still need help? We’re happy to hop on a call.</p>
            </div>
            <div class="space-y-3">
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  How fast do vendors respond on Invi8?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">Most respond within 2-3 hours. Listings with “Instant hold” can be reserved immediately.</p>
              </details>
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  Do you charge couples to book?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">No booking fees. Vendors pay a small platform fee that’s included in their pricing.</p>
              </details>
              <details class="group rounded-2xl bg-white/5 p-4">
                <summary class="flex cursor-pointer items-center justify-between text-sm font-semibold">
                  How do vendors get paid?
                  <span class="text-brand">+</span>
                </summary>
                <p class="mt-2 text-sm text-gray-300">We release payouts quickly after milestones. Vendors always see clear schedules and amounts.</p>
              </details>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'ContactView'
}
</script>
